/**
 * Standard T-Hangar Row Generate Functions
 *
 * Generates building and unit configurations from T-hangar parameters.
 */

import type { GeneratedBuilding, GeneratedUnit, DoorSpec } from '../types'
import type { THangarParams } from './params'
import { calculateUnitPitch, createDefaultTHangarDoorSpec } from './params'
import { calculateRowLength, calculateBuildingDepth, calculateTHangarUnitOffsets, isUnitSouthFacing } from './calculations'
import { createTHangarEdges } from './edges'

/**
 * Generate building configuration from T-hangar parameters
 */
export function generateBuilding(params: THangarParams): GeneratedBuilding {
  const rotationRadians = (params.bearing_deg * Math.PI) / 180
  const rowLength = calculateRowLength(params)
  const buildingDepth = calculateBuildingDepth(params)
  const unitPitch = calculateUnitPitch(params)

  return {
    name: params.name,
    code: params.code,
    type: 't_hangar_row',
    exterior_width: rowLength,
    exterior_depth: buildingDepth,
    exterior_height: params.height_ft,
    latitude: params.latitude,
    longitude: params.longitude,
    rotation: rotationRadians,
    door_width: Math.min(params.default_door_width_ft, params.unit_width_ft - 4),
    door_height: params.default_door_height_ft,
    settings: {
      floor_color: params.floor_color,
      wall_color: params.wall_color,
      doors: [], // Doors are per-unit in T-hangars
    },
    type_config: {
      template: 'standard_t_hangar',
      unit_count: params.unit_count,
      unit_width_ft: params.unit_width_ft,
      wing_depth_ft: params.wing_depth_ft,
      tail_width_ft: params.tail_width_ft,
      unit_pitch_ft: unitPitch,
      wall_thickness_ft: params.wall_thickness_ft,
      default_door_type: params.default_door_type,
      default_door_width_ft: params.default_door_width_ft,
      default_door_height_ft: params.default_door_height_ft,
      unit_overrides: params.unit_overrides?.length ? params.unit_overrides : undefined,
    },
  }
}

/**
 * Generate unit configurations from building and T-hangar parameters
 */
export function generateUnits(
  _building: GeneratedBuilding,
  params: THangarParams
): GeneratedUnit[] {
  const units: GeneratedUnit[] = []
  const offsets = calculateTHangarUnitOffsets(params)
  const buildingDepth = calculateBuildingDepth(params)

  // Default door spec for all units
  const defaultDoorSpec = createDefaultTHangarDoorSpec(params)

  for (let i = 0; i < params.unit_count; i++) {
    const unitCode = `${params.code}-${String(i + 1).padStart(2, '0')}`
    const southFacing = isUnitSouthFacing(i)

    // Check for per-unit overrides
    const unitOverride = params.unit_overrides?.find(o => o.index === i)
    const typeOverride = unitOverride?.type

    // Resolve door spec: per-unit override → default
    const doorSpec: DoorSpec = unitOverride?.doors?.front?.[0] ?? defaultDoorSpec

    // Create T-hangar edges with resolved door spec
    const tHangarEdges = createTHangarEdges(i, params.unit_count, params, params.code, doorSpec)

    // T-shape area: wing_bay + tail_bay
    // Interior width = unit_width (the wing span)
    // Interior depth = building_depth - 2*wall (the full depth of the unit)
    const interiorDepth = buildingDepth - 2 * params.wall_thickness_ft

    const unit: GeneratedUnit = {
      code: unitCode,
      type: typeOverride || 't_hangar',
      capacity_strategy: 'single',
      interior_width: params.unit_width_ft,
      interior_depth: interiorDepth,
      interior_height: params.height_ft,
      door_width: doorSpec.width_ft,
      door_height: doorSpec.height_ft,
      door_type: doorSpec.type,
      offset_x: offsets[i],
      offset_y: 0,
      // South-facing units: rotation 0 (door faces south / +Z in scene)
      // North-facing units: rotation PI (door faces north / -Z in scene)
      unit_rotation: southFacing ? 0 : Math.PI,
      edges: undefined, // Box edges not used for T-hangars
      tHangarEdges,
    }

    units.push(unit)
  }

  return units
}
